import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { downloadInvoice } from '../../utils/invoiceDownload';
import InvoicePreview from '../shared/InvoicePreview';
import {
  Search,
  Filter,
  Eye,
  FileText,
  FileSpreadsheet,
  Calendar,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

/**
 * Payments Component - DISPLAYS ONLY HISTORICALLY PAID INVOICES
 * 
 * This component shows ONLY invoices that have been fully paid and completed.
 * All invoices displayed here must have:
 * - status: 'paid'
 * - paymentDate: actual payment completion date
 * - transactionId: proof of payment
 * 
 * NO pending, rejected, or unpaid invoices should appear here.
 */
const Payments = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [highlightedPaymentId, setHighlightedPaymentId] = useState(null);

  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState(null);
  const [refundType, setRefundType] = useState('full');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  // Fetch real paid invoices from backend API
  useEffect(() => {
    // Fetch real paid invoices from backend API
    const fetchPaidInvoices = async () => {
      try {
        setLoading(true);
        console.log('🔍 Fetching paid invoices...');

        // Build query parameters
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (dateRange.start) params.append('startDate', dateRange.start);
        if (dateRange.end) params.append('endDate', dateRange.end);
        params.append('page', currentPage.toString());
        params.append('limit', paymentsPerPage.toString());

        console.log('📡 API Request:', `/invoices/paid?${params.toString()}`);
        const response = await api.get(`/invoices/paid?${params.toString()}`);
        const data = response.data;
        console.log('📦 API Response:', data);
        console.log('📦 API Response Data:', data.data);
        console.log('📦 Invoices Array:', data.data?.invoices);
        
        // 🔍 DEBUG: Check refund status for INV-2025-8589
        const invoice8589 = data.data?.invoices?.find(inv => inv.invoiceNumber === 'INV-2025-8589');
        if (invoice8589) {
          console.log('🔍 INV-2025-8589 REFUND DATA:', {
            invoiceNumber: invoice8589.invoiceNumber,
            status: invoice8589.status,
            refundStatus: invoice8589.refundStatus,
            refundAmount: invoice8589.refundAmount,
            refundDate: invoice8589.refundDate,
            refundReason: invoice8589.refundReason
          });
        }
        
        if (data.success) {
          // ✅ ENFORCE: Only show invoices with 'paid' status
          const paidInvoicesOnly = data.data.invoices.filter(payment => payment.status === 'paid');
          console.log('📦 Filtered Paid Invoices:', paidInvoicesOnly);
          
          // ✅ DOUBLE VALIDATION: Ensure all invoices meet paid criteria
          const validPaidInvoices = getValidPaidInvoices(paidInvoicesOnly);
          console.log('📦 Valid Paid Invoices:', validPaidInvoices);
          
          setPayments(validPaidInvoices);
          setFilteredPayments(validPaidInvoices);
          console.log('📦 State updated with payments:', validPaidInvoices.length);
          
          // Successfully fetched paid invoices from API
        } else {
          console.error('❌ API returned error:', data.message);
          setPayments([]);
          setFilteredPayments([]);
        }
      } catch (error) {
        console.error('❌ Error fetching paid invoices:', error);
        console.error('❌ Error details:', error.response?.data);
        setPayments([]);
        setFilteredPayments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPaidInvoices();

  }, [user, currentPage, searchQuery, dateRange.start, dateRange.end]);

  // Check for highlight parameter to highlight specific payment
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedPaymentId(highlightId);
      // Auto-remove highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedPaymentId(null);
        // Remove highlight parameter from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('highlight');
        setSearchParams(newParams, { replace: true });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // Search and filter functionality
  useEffect(() => {
    let filtered = payments;

    // ✅ CRITICAL: Ensure only paid invoices are displayed
    filtered = filtered.filter(payment => payment.status === 'paid');

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(payment =>
        payment.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(payment => {
        const dueDate = new Date(payment.dueDate);
        const start = dateRange.start ? new Date(dateRange.start) : null;
        const end = dateRange.end ? new Date(dateRange.end) : null;
        
        if (start && end) {
          return dueDate >= start && dueDate <= end;
        } else if (start) {
          return dueDate >= start;
        } else if (end) {
          return dueDate <= end;
        }
        return true;
      });
    }

    setFilteredPayments(filtered);
  }, [payments, searchQuery, dateRange]);

  // Pagination logic
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);
  const startIndex = (currentPage - 1) * paymentsPerPage;
  const endIndex = startIndex + paymentsPerPage;
  const currentPayments = filteredPayments.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateRange]);



  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredPayments].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (key === 'customer') {
        aValue = a.customer.name;
        bValue = b.customer.name;
      } else if (key === 'supplier') {
        aValue = a.supplier.name;
        bValue = b.supplier.name;
      } else if (key === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (key === 'dueDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPayments(sorted);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };



  const legacyDownloadPDF = (invoice) => {
    // Generate PDF using browser print functionality - opens new window with print template
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoiceNumber || 'Invoice'}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          
          body { 
            font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; 
            margin: 40px; 
            color: #1f2937;
            background: #ffffff;
            line-height: 1.6;
          }
          
          .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          
          .invoice-header {
            text-align: center;
            padding: 40px 30px;
            border-bottom: 1px solid #e2e8f0;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          }
          
          .invoice-title {
            font-size: 36px;
            font-weight: 300;
            color: #1f2937;
            margin: 0 0 12px 0;
            letter-spacing: 2px;
          }
          
          .title-line {
            width: 80px;
            height: 2px;
            background: #94a3b8;
            margin: 0 auto 24px auto;
          }
          
          .invoice-meta {
            display: flex;
            justify-content: center;
            gap: 40px;
            font-size: 14px;
            color: #64748b;
          }
          
          .meta-item {
            text-align: center;
          }
          
          .meta-label {
            font-size: 11px;
            font-weight: 600;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 4px;
          }
          
          .meta-value {
            font-weight: 500;
            color: #475569;
          }
          
          .company-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 32px;
            padding: 40px;
          }
          
          .company-card {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 32px;
          }
          
          .company-header {
            display: flex;
            align-items: center;
            margin-bottom: 24px;
          }
          
          .company-dot {
            width: 8px;
            height: 8px;
            background: #64748b;
            border-radius: 50%;
            margin-right: 12px;
          }
          
          .company-card h3 {
            font-size: 12px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin: 0;
          }
          
          .company-card p {
            margin: 6px 0;
            font-size: 14px;
            color: #475569;
            line-height: 1.5;
          }
          
          .company-card p:first-of-type {
            font-weight: 600;
            color: #1e293b;
            font-size: 16px;
            margin-top: 0;
          }
          
          .line-items-section {
            padding: 0 40px 40px 40px;
          }
          
          .line-items-header {
            display: flex;
            align-items: center;
            margin-bottom: 24px;
          }
          
          .line-items-section h3 {
            font-size: 12px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin: 0;
          }
          
          .line-items-table {
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          }
          
          .line-items-table table {
            width: 100%;
            border-collapse: collapse;
          }
          
          .line-items-table th {
            background: #f8fafc;
            border-bottom: 1px solid #e2e8f0;
            padding: 16px 24px;
            text-align: left;
            font-size: 11px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .line-items-table td {
            padding: 16px 24px;
            border-bottom: 1px solid #f1f5f9;
            font-size: 14px;
            color: #475569;
          }
          
          .line-items-table tr:last-child td {
            border-bottom: none;
          }
          
          .line-items-table .amount {
            text-align: right;
            font-weight: 500;
          }
          
          .empty-state {
            text-align: center;
            padding: 48px 24px;
            color: #94a3b8;
          }
          
          .empty-state p {
            font-size: 14px;
            font-weight: 500;
            margin-bottom: 4px;
          }
          
          .empty-state .subtitle {
            font-size: 12px;
            color: #cbd5e1;
          }
          
          .totals-section {
            padding: 0 40px 40px 40px;
            display: flex;
            justify-content: flex-end;
          }
          
          .totals-card {
            width: 384px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            padding: 32px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            font-size: 14px;
          }
          
          .total-row.grand-total {
            border-top: 1px solid #cbd5e1;
            padding-top: 24px;
            margin-top: 16px;
            font-size: 18px;
            font-weight: 600;
            color: #1e293b;
          }
          
          .grand-total .total-amount {
            font-size: 24px;
            font-weight: 700;
          }
          
          .no-print {
            text-align: center;
            padding: 24px;
            background: #f8fafc;
            border-top: 1px solid #e2e8f0;
          }
          
          .no-print button {
            background: #475569;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            margin: 0 8px;
            font-size: 14px;
            font-weight: 500;
          }
          
          .no-print button:hover {
            background: #334155;
          }
          
          @page {
            margin: 0.5in !important;
            size: A4 !important;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            body { 
              margin: 0 !important; 
              padding: 0 !important;
              font-size: 12px;
              background: white !important;
            }
            html {
              background: white !important;
            }
            .no-print { display: none !important; }
            .invoice-container { 
              max-width: none !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .invoice-header { 
              background: white !important;
              border-bottom: 1px solid #e2e8f0;
            }
            .company-section { 
              page-break-inside: avoid;
            }
            .line-items-table { 
              page-break-inside: avoid;
            }
            .totals-section { 
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <!-- Invoice Header -->
          <div class="invoice-header">
            <h1 class="invoice-title">INVOICE</h1>
            <div class="title-line"></div>
            <div class="invoice-meta">
              <div class="meta-item">
                <div class="meta-label">Invoice Date</div>
                <div class="meta-value">${invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Invoice Number</div>
                <div class="meta-value">${invoice.invoiceNumber || '[Not set]'}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Due Date</div>
                <div class="meta-value">${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB') : '[Not set]'}</div>
              </div>
            </div>
            ${invoice.status || invoice.approvalStatus ? `
            <div style="margin-top: 16px; text-align: center;">
              <span style="display: inline-block; padding: 6px 16px; background: #fef3c7; color: #92400e; border-radius: 8px; font-size: 11px; font-weight: 600; text-transform: uppercase;">
                ${(invoice.approvalStatus || invoice.status || '').replace(/_/g, ' ')}
              </span>
            </div>
            ` : ''}
            ${invoice.poNumber ? `
            <div style="margin-top: 12px; text-align: center; font-size: 12px; color: #64748b;">
              <strong>PO Number:</strong> ${invoice.poNumber}
            </div>
            ` : ''}
          </div>
          
          <!-- Company Information -->
          <div class="company-section">
            <div class="company-card">
              <div class="company-header">
                <div class="company-dot"></div>
                <h3>From</h3>
              </div>
              <p><strong>${invoice.sender?.companyName || invoice.supplier?.name || '[Company Name]'}</strong></p>
              ${invoice.sender?.firstName || invoice.sender?.lastName ? `<p>${invoice.sender?.firstName || ''} ${invoice.sender?.lastName || ''}</p>` : ''}
              <p>${invoice.sender?.email || invoice.supplier?.email || '[Email]'}</p>
              ${invoice.sender?.address || invoice.supplier?.address ? `<p>${invoice.sender?.address || invoice.supplier?.address}</p>` : ''}
              ${invoice.sender?.city ? `<p>${invoice.sender.city}</p>` : ''}
              ${invoice.sender?.state ? `<p>${invoice.sender.state}</p>` : ''}
              ${invoice.sender?.zipCode ? `<p>${invoice.sender.zipCode}</p>` : ''}
              ${invoice.sender?.country ? `<p>${invoice.sender.country}</p>` : ''}
            </div>
            
            <div class="company-card">
              <div class="company-header">
                <div class="company-dot"></div>
                <h3>To</h3>
              </div>
              <p><strong>${invoice.receiver?.companyName || invoice.customer?.name || '[Company Name]'}</strong></p>
              ${invoice.receiver?.firstName || invoice.receiver?.lastName ? `<p>${invoice.receiver?.firstName || ''} ${invoice.receiver?.lastName || ''}</p>` : ''}
              <p>${invoice.receiver?.email || invoice.customer?.email || '[Email]'}</p>
              ${invoice.receiver?.address || invoice.customer?.address ? `<p>${invoice.receiver?.address || invoice.customer?.address}</p>` : ''}
              ${invoice.receiver?.city || invoice.customer?.city ? `<p>${invoice.receiver?.city || invoice.customer?.city}</p>` : ''}
              ${invoice.receiver?.state ? `<p>${invoice.receiver.state}</p>` : ''}
              ${invoice.receiver?.zipCode || invoice.customer?.zipCode ? `<p>${invoice.receiver?.zipCode || invoice.customer?.zipCode}</p>` : ''}
              ${invoice.receiver?.country || invoice.customer?.country ? `<p>${invoice.receiver?.country || invoice.customer?.country}</p>` : ''}
            </div>
          </div>
          
          <!-- Line Items -->
          <div class="line-items-section">
            <div class="line-items-header">
              <div class="company-dot"></div>
              <h3>Line Items</h3>
            </div>
            <div class="line-items-table">
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style="text-align: center;">Qty</th>
                    <th style="text-align: right;">Unit Price</th>
                    <th style="text-align: right;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoice.lineItems && invoice.lineItems.length > 0 ? 
                    invoice.lineItems.map(item => {
                      const subtotal = (item.quantity || 0) * (item.unitPrice || 0);
                      const discount = subtotal * ((item.discount || 0) / 100);
                      const afterDiscount = subtotal - discount;
                      const tax = afterDiscount * ((item.taxRate || 0) / 100);
                      const total = afterDiscount + tax;
                      
                      const formattedUnitPrice = item.unitPrice < 0 ? `(£${Math.abs(item.unitPrice).toFixed(2)})` : `£${(item.unitPrice || 0).toFixed(2)}`;
                      const formattedTotal = total < 0 ? `(£${Math.abs(total).toFixed(2)})` : `£${total.toFixed(2)}`;
                      return `
                        <tr>
                          <td>${item.description || '[Item description]'}</td>
                          <td style="text-align: center;">${item.quantity || 0}</td>
                          <td class="amount">${formattedUnitPrice}</td>
                          <td class="amount">${formattedTotal}</td>
                        </tr>
                      `;
                    }).join('') : 
                    '<tr><td colspan="4"><div class="empty-state"><p>No line items found</p></div></td></tr>'
                  }
                </tbody>
              </table>
            </div>
          </div>
          
          <!-- Totals -->
          <div class="totals-section">
            <div class="totals-card">
              <div class="total-row">
                <span style="color: #64748b;">Subtotal:</span>
                <span style="font-weight: 500;">£${(parseFloat(invoice.subtotal) || 0).toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span style="color: #64748b;">Tax:</span>
                <span style="font-weight: 500;">£${(parseFloat(invoice.tax) || 0).toFixed(2)}</span>
              </div>
              ${invoice.discountAmount && parseFloat(invoice.discountAmount) > 0 ? `
              <div class="total-row" style="background: #f9f9f9; margin: 8px -16px; padding: 8px 16px; border-top: 1px dashed #999; border-bottom: 1px dashed #999;">
                <span style="color: #555; font-style: italic;">
                  ✨ Early Payment Discount
                  ${invoice.discountSourceInvoice?.invoiceNumber ? `<br><span style="font-size: 10px; font-style: normal;">(from ${invoice.discountSourceInvoice.invoiceNumber})</span>` : ''}:
                </span>
                <span style="color: #555; font-style: italic; font-weight: 500;">-£${(parseFloat(invoice.discountAmount)).toFixed(2)}</span>
              </div>
              ` : ''}
              ${invoice.refundAmount && parseFloat(invoice.refundAmount) > 0 ? `
              <div class="total-row" style="background: #fee; margin: 8px -16px; padding: 8px 16px; border-top: 1px dashed #f99; border-bottom: 1px dashed #f99;">
                <span style="color: #c00; font-weight: 600;">Refunded:</span>
                <span style="color: #c00; font-weight: 600;">-£${(parseFloat(invoice.refundAmount)).toFixed(2)}</span>
              </div>
              ` : ''}
              <div class="total-row grand-total">
                <span>Total:</span>
                <span class="total-amount">£${(parseFloat(invoice.total) || parseFloat(invoice.amount) || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          ${invoice.notes ? `
          <div style="padding: 0 40px 20px 40px;">
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
              <h4 style="font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin: 0 0 12px 0;">Notes:</h4>
              <p style="margin: 0; font-size: 13px; color: #475569; line-height: 1.6;">${invoice.notes}</p>
            </div>
          </div>
          ` : ''}
          
          ${invoice.poNumber ? `
          <div style="padding: 0 40px 20px 40px;">
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
              <h4 style="font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin: 0 0 12px 0;">Purchase Order:</h4>
              <p style="margin: 0; font-size: 13px; color: #475569; line-height: 1.6;">${invoice.poNumber}</p>
            </div>
          </div>
          ` : ''}
          
          ${invoice.terms ? `
          <div style="padding: 0 40px 20px 40px;">
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
              <h4 style="font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin: 0 0 12px 0;">Terms & Conditions:</h4>
              <p style="margin: 0; font-size: 13px; color: #475569; line-height: 1.6;">${invoice.terms}</p>
            </div>
          </div>
          ` : ''}
          
          <div class="no-print">
            <p>This invoice can be printed or saved as PDF using your browser's print function.</p>
            <button onclick="window.print()">Print / Save as PDF</button>
            <button onclick="window.close()">Close</button>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const legacyDownloadExcel = (invoice) => {
    // Generate CSV for invoice
    const csvContent = [
      ['Invoice Number', 'Customer', 'Amount', 'Status', 'Due Date', 'Payment Date'],
      [
        invoice.invoiceNumber || '',
        invoice.customer?.name || invoice.receiver?.companyName || '',
        `£${(invoice.amount || 0).toFixed(2)}`,
        (invoice.status || '').replace(/-/g, ' ').toUpperCase(),
        invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB') : '',
        invoice.paymentDate ? new Date(invoice.paymentDate).toLocaleDateString('en-GB') : ''
      ]
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
    link.download = `invoice-${invoice.invoiceNumber || 'unknown'}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async (invoice, extraOptions = {}) => {
    try {
      await downloadInvoice(invoice, 'pdf', { setLoading, ...extraOptions });
    } catch (error) {
      console.warn('Payments PDF download failed, using legacy renderer', error);
      legacyDownloadPDF(invoice);
    }
  };

  const handleDownloadExcel = async (invoice, extraOptions = {}) => {
    try {
      await downloadInvoice(invoice, 'csv', { setLoading, ...extraOptions });
    } catch (error) {
      console.warn('Payments CSV download failed, using legacy renderer', error);
      legacyDownloadExcel(invoice);
    }
  };

  const handlePreviewInvoice = async (payment) => {
    console.log('👁️ Opening invoice preview for:', payment.invoiceNumber);
    
    // ✅ CRITICAL: Fetch full invoice data with guestRecipient JSONB field
    let fullInvoiceData = payment;
    
    try {
      const fullInvoiceResponse = await api.get(`/invoices/${payment.id}`);
      const fullInvoice = fullInvoiceResponse.data.invoice || fullInvoiceResponse.data.data?.invoice || fullInvoiceResponse.data;
      
      console.log('✅ Full invoice data fetched:', fullInvoice);
      
      // Merge full data with payment data
      fullInvoiceData = {
        ...payment,
        ...fullInvoice,
        // Preserve supplier/customer from payment list for display consistency
        supplier: payment.supplier,
        customer: payment.customer
      };
    } catch (fetchError) {
      console.error('⚠️ Could not fetch full invoice data, using basic payment data:', fetchError);
      // Continue with basic payment data if fetch fails
    }
    
    // Transform payment data to match InvoicePreview component expectations
    const transformedInvoice = {
      id: fullInvoiceData.id,
      invoiceNumber: fullInvoiceData.invoiceNumber,
      status: fullInvoiceData.status,
      approvalStatus: fullInvoiceData.approvalStatus,
      dueDate: fullInvoiceData.dueDate,
      paymentDate: fullInvoiceData.paymentDate,
      paymentMethod: fullInvoiceData.paymentMethod,
      subtotal: parseFloat(fullInvoiceData.subtotal) || parseFloat(fullInvoiceData.amount) || 0,
      tax: parseFloat(fullInvoiceData.tax) || 0,
      total: parseFloat(fullInvoiceData.total) || parseFloat(fullInvoiceData.amount) || 0,
      currency: fullInvoiceData.currency || 'GBP',
      lineItems: fullInvoiceData.items || fullInvoiceData.lineItems || [], // Map items to lineItems for InvoicePreview component
      notes: fullInvoiceData.notes || '',
      terms: fullInvoiceData.terms || '',
      poNumber: fullInvoiceData.poNumber || '',
      createdAt: fullInvoiceData.createdAt,
      metadata: fullInvoiceData.metadata || {},
      // For InvoicePreview component - sender info (who sent the invoice)
      sender: fullInvoiceData.sender || (payment.supplier ? {
        companyName: payment.supplier.name,
        firstName: payment.supplier.name.split(' ')[0] || '',
        lastName: payment.supplier.name.split(' ').slice(1).join(' ') || '',
        email: payment.supplier.email,
        phone: '',
        address: payment.supplier.address || '',
        city: payment.supplier.city || '',
        state: payment.supplier.state || '',
        zipCode: payment.supplier.zipCode || '',
        country: payment.supplier.country || ''
      } : {
        companyName: user?.companyName || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        address: user?.address || '',
        city: user?.city || '',
        state: user?.state || '',
        zipCode: user?.zipCode || '',
        country: user?.country || ''
      }),
      // For InvoicePreview component - receiver info (who received the invoice)
      receiver: fullInvoiceData.receiver || (payment.customer ? {
        companyName: payment.customer.name,
        firstName: payment.customer.name.split(' ')[0] || '',
        lastName: payment.customer.name.split(' ').slice(1).join(' ') || '',
        email: payment.customer.email,
        phone: '',
        address: payment.customer.address || '',
        city: payment.customer.city || '',
        state: payment.customer.state || '',
        zipCode: payment.customer.zipCode || '',
        country: payment.customer.country || ''
      } : null),
      // ✅ CRITICAL: Guest recipient data from JSONB field (has full address)
      guestRecipient: fullInvoiceData.guestRecipient || (payment.customer ? {
        companyName: payment.customer.name,
        firstName: payment.customer.name?.split(' ')[0] || '',
        lastName: payment.customer.name?.split(' ').slice(1).join(' ') || '',
        email: payment.customer.email,
        phone: '',
        address: payment.customer.address || '',
        city: payment.customer.city || '',
        state: payment.customer.state || '',
        zipCode: payment.customer.zipCode || '',
        country: payment.customer.country || ''
      } : null),
      // Add refund data
      refundAmount: fullInvoiceData.refundAmount,
      refundStatus: fullInvoiceData.refundStatus,
      refundDate: fullInvoiceData.refundDate,
      refundReason: fullInvoiceData.refundReason,
      stripeRefundId: fullInvoiceData.stripeRefundId,
      // Add discount data
      discountAmount: fullInvoiceData.discountAmount,
      discountSourceInvoice: fullInvoiceData.discountSourceInvoice,
      // Add attachments from metadata
      attachments: fullInvoiceData.metadata?.attachments || []
    };

    console.log('🎯 Transformed invoice for preview:', transformedInvoice);
    console.log('📍 Guest recipient address:', transformedInvoice.guestRecipient);

    setPreviewInvoice(transformedInvoice);
    setShowPreview(true);
  };

  // Function to handle export of selected invoices
  const handleExportSelected = () => {
    if (selectedInvoices.length === 0) {
      alert('Please select invoices to export');
      return;
    }

    // Get the selected invoice data
    const selectedInvoiceData = payments.filter(payment => 
      selectedInvoices.includes(payment.id)
    );

    if (selectedInvoiceData.length === 0) {
      alert('No invoice data found for export');
      return;
    }

    // Create CSV content
    const csvHeaders = [
      'Invoice Number',
      'Customer/Supplier',
      'Type',
      'Amount',
      'Status',
      'Due Date',
      'Payment Date',
      'Description',
      'Payment Method',
      'Transaction ID',
      'Notes'
    ];

    const csvRows = selectedInvoiceData.map(payment => [
      payment.invoiceNumber,
      payment.customer.type === 'customer' ? payment.customer.name : payment.supplier.name,
      payment.customer.type === 'customer' ? 'Customer' : 'Supplier',
      formatCurrency(payment.amount),
      'Paid',
      formatDate(payment.dueDate),
      payment.paymentDate ? formatDate(payment.paymentDate) : '',
      payment.description,
      payment.paymentMethod || '',
      payment.transactionId || '',
      payment.notes || ''
    ]);

    // Convert to CSV format
    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => 
        row.map(cell => `"${cell}"`).join(',')
      )
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `paid-invoices-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clear selection
    setSelectedInvoices([]);
            // Successfully exported invoices to CSV
  };

  const handleDownloadSelectedPDF = () => {
    if (selectedInvoices.length === 0) {
      alert('Please select invoices to download');
      return;
    }

    const selectedInvoiceData = payments.filter(payment =>
      selectedInvoices.includes(payment.id)
    );

    if (selectedInvoiceData.length === 0) {
      alert('No invoice data found for download');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Unable to open download window. Please allow pop-ups and try again.');
      return;
    }

    const styles = `
      body { font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; margin: 32px; color: #1f2937; }
      h1 { font-size: 24px; margin-bottom: 24px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
      th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-size: 12px; }
      th { background: #f8fafc; text-transform: uppercase; font-weight: 600; color: #475569; }
      tr:nth-child(even) { background: #f9fafb; }
      .meta { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 12px; color: #64748b; }
      .footer { margin-top: 32px; font-size: 12px; color: #94a3b8; }
      .section { page-break-inside: avoid; margin-bottom: 32px; }
      .section h2 { font-size: 18px; margin-bottom: 12px; }
    `;

    const rows = selectedInvoiceData.map(payment => {
      const paymentDate = payment.paymentDate ? new Date(payment.paymentDate).toLocaleString('en-GB') : '';
      const dueDate = payment.dueDate ? new Date(payment.dueDate).toLocaleDateString('en-GB') : '';
      const amount = typeof payment.amount === 'number'
        ? `£${payment.amount.toFixed(2)}`
        : `£${parseFloat(payment.amount || 0).toFixed(2)}`;

      return `
        <tr>
          <td>${payment.invoiceNumber || ''}</td>
          <td>${payment.customer?.type === 'customer' ? 'Sent' : 'Received'}</td>
          <td>${payment.customer?.type === 'customer'
            ? payment.customer?.name || ''
            : payment.supplier?.name || ''}</td>
          <td>${amount}</td>
          <td>Paid</td>
          <td>${dueDate}</td>
          <td>${paymentDate}</td>
          <td>${payment.paymentMethod || ''}</td>
          <td>${payment.transactionId || ''}</td>
          <td>${payment.notes || ''}</td>
        </tr>
      `;
    }).join('');

    const summaryHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Selected Paid Invoices</title>
          <style>${styles}</style>
        </head>
        <body>
          <div class="section">
            <h1>Selected Paid Invoices</h1>
            <div class="meta">
              <span>Total invoices: ${selectedInvoiceData.length}</span>
              <span>Generated: ${new Date().toLocaleString('en-GB')}</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Direction</th>
                  <th>Company</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Payment Date</th>
                  <th>Payment Method</th>
                  <th>Transaction ID</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>
            <div class="footer">
              Generated via Receevit® • ${window.location.origin}
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(summaryHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Function to toggle invoice selection
  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  // ✅ VALIDATION: Ensure invoice meets paid criteria
  const validatePaidInvoice = (invoice) => {
    // More flexible validation - only require status and paymentDate
    const isValid = (
      invoice.status === 'paid' &&
      invoice.paymentDate
    );
    
    if (!isValid) {
      console.log('❌ Invoice validation failed:', {
        id: invoice.id,
        status: invoice.status,
        paymentDate: invoice.paymentDate,
        transactionId: invoice.transactionId,
        approvalStatus: invoice.approvalStatus,
        invoice
      });
    } else {
      console.log('✅ Invoice validation passed:', {
        id: invoice.id,
        status: invoice.status,
        paymentDate: invoice.paymentDate,
        transactionId: invoice.transactionId,
        approvalStatus: invoice.approvalStatus
      });
    }
    
    return isValid;
  };

  // ✅ FILTER: Get only valid paid invoices
  const getValidPaidInvoices = (invoices) => {
    return invoices.filter(invoice => validatePaidInvoice(invoice));
  };

  // Status badge for paid invoices
  const getStatusBadge = (payment) => {
    // Check for refund status first
    if (payment.refundStatus === 'full') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <RefreshCw className="w-3 h-3 mr-1" />
          Fully Refunded
        </span>
      );
    }

    if (payment.refundStatus === 'partial') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <RefreshCw className="w-3 h-3 mr-1" />
          Partially Refunded
        </span>
      );
    }

    // Default paid badge
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Paid
      </span>
    );
  };

  const exportToCSV = () => {
    const headers = [
      'Invoice Number',
      'Customer/Supplier',
      'Type',
      'Sent/Received',
      'Amount',
      'Status',
      'Due Date',
      'Payment Date',
      'Description',
      'Payment Method',
      'Transaction ID',
      'Notes'
    ];

    const csvData = filteredPayments.map(payment => [
      payment.invoiceNumber,
      payment.customer.type === 'customer' ? payment.customer.name : payment.supplier.name,
      payment.customer.type === 'customer' ? 'Customer' : 'Supplier',
      payment.customer.type === 'customer' ? 'Sent' : 'Received',
      formatCurrency(payment.amount),
      'Paid',
      formatDate(payment.dueDate),
      payment.paymentDate ? formatDate(payment.paymentDate) : '',
      payment.description,
      payment.paymentMethod || '',
      payment.transactionId || '',
      payment.notes || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `paid-invoices-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDateRange({ start: '', end: '' });
  };

  const formatCurrency = (amount) => {
    // Database stores amounts in pounds as DECIMAL(10,2)
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB');
  };

  // Refund modal handlers
  const handleOpenRefundModal = (payment) => {
    console.log('💰 Opening refund modal for:', payment.invoiceNumber);
    setRefundInvoice(payment);
    setRefundType('full');
    setRefundAmount(payment.amount);
    setRefundReason('');
    setShowRefundModal(true);
  };

  const handleCloseRefundModal = () => {
    setShowRefundModal(false);
    setRefundInvoice(null);
    setRefundType('full');
    setRefundAmount('');
    setRefundReason('');
  };

  const handleSubmitRefund = async () => {
    try {
      // Validation
      if (!refundReason.trim()) {
        alert('Please provide a reason for the refund');
        return;
      }

      if (refundType === 'partial') {
        const refundAmountNum = parseFloat(refundAmount);
        const totalPaid = parseFloat(refundInvoice.amount);
        const alreadyRefunded = parseFloat(refundInvoice.refundAmount || 0);
        const remainingBalance = totalPaid - alreadyRefunded;

        if (isNaN(refundAmountNum) || refundAmountNum <= 0) {
          alert('Please enter a valid refund amount greater than £0');
          return;
        }

        if (refundAmountNum > remainingBalance) {
          alert(`Cannot refund £${refundAmountNum.toFixed(2)}. Remaining balance: £${remainingBalance.toFixed(2)}`);
          return;
        }
      }

      setRefundLoading(true);
      console.log('💰 Submitting refund:', {
        invoiceId: refundInvoice.id,
        refundAmount: refundType === 'full' ? refundInvoice.amount : refundAmount,
        refundReason,
        refundType
      });

      try {
        // Amount is already in pounds (DECIMAL), no conversion needed
        const fullRefundPounds = parseFloat(refundInvoice.amount);
        const partialRefundPounds = parseFloat(refundAmount);
        
        const refundData = {
          refundAmount: refundType === 'full' ? fullRefundPounds : partialRefundPounds,
          refundReason: refundReason.trim(),
          refundType: refundType
        };

        const response = await api.post(`/api/invoices/${refundInvoice.id}/refund`, refundData);

        if (response.data.success) {
          console.log('✅ Refund processed successfully:', response.data);
          
          // Show success message
          const message = refundType === 'full' 
            ? `Full refund of ${formatCurrency(refundInvoice.amount)} processed successfully. Customer will receive funds within 5-10 business days.`
            : `Partial refund of ${formatCurrency(refundAmount)} processed successfully. Customer will receive funds within 5-10 business days.`;
          
          alert(message);

          // Close modal
          handleCloseRefundModal();

          // Refresh payments list
          window.location.reload();
        } else {
          alert('Refund failed: ' + (response.data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('❌ Refund error:', error);
        
        // If we get a 403 error, try direct API Gateway as fallback
        if (error.response?.status === 403 && error.response?.data?.message?.includes('Invalid key-value pair')) {
          console.log('🔄 Proxy failed, trying direct API Gateway...');
          try {
            const token = localStorage.getItem('token');
            const directResponse = await fetch(
              `https://5e460pfqz0.execute-api.eu-west-2.amazonaws.com/prod/api/invoices/${refundInvoice.id}/refund`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  refundAmount: refundType === 'full' ? parseFloat(refundInvoice.amount) : parseFloat(refundAmount),
                  refundReason: refundReason.trim(),
                  refundType: refundType
                })
              }
            );

            const directData = await directResponse.json();
            
            if (directResponse.ok && directData.success) {
              console.log('✅ Refund processed via direct API Gateway:', directData);
              
              const message = refundType === 'full' 
                ? `Full refund of ${formatCurrency(refundInvoice.amount)} processed successfully. Customer will receive funds within 5-10 business days.`
                : `Partial refund of ${formatCurrency(refundAmount)} processed successfully. Customer will receive funds within 5-10 business days.`;
              
              alert(message);
              handleCloseRefundModal();
              window.location.reload();
            } else {
              throw new Error(directData.error || directData.message || 'Refund failed');
            }
          } catch (fallbackError) {
            console.error('❌ Direct API Gateway also failed:', fallbackError);
            alert('Failed to process refund: ' + (fallbackError.message || 'Unknown error'));
          }
        } else {
          alert('Failed to process refund: ' + (error.response?.data?.message || error.message));
        }
      } finally {
        setRefundLoading(false);
      }
    } catch (error) {
      console.error('❌ Outer refund error:', error);
      setRefundLoading(false);
    }
  };

  const calculateRemainingBalance = (payment) => {
    const totalPaid = parseFloat(payment.amount || 0);
    const alreadyRefunded = parseFloat(payment.refundAmount || 0);
    return totalPaid - alreadyRefunded;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Paid Invoices</h1>
          <p className="text-gray-600">View and export all historically paid and completed invoices</p>
          <p className="text-xs text-gray-500 mt-1">Only invoices with confirmed payment status are displayed</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedInvoices.length > 0 && (
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExportSelected}
                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-blue-500 text-blue-600 bg-white hover:bg-blue-50 transition"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export ({selectedInvoices.length})
              </button>
              <button
                onClick={handleDownloadSelectedPDF}
                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-slate-500 text-slate-600 bg-white hover:bg-slate-50 transition"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF ({selectedInvoices.length})
              </button>
            </div>
          )}
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export All
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>



      {/* Search and Filters - Only Paid Invoices */}
      <div className="bg-white rounded-lg border shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Search */}
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Invoice, Customer/Supplier, or Company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Clear Filters */}
          <div>
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        {showFilters && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <div className="text-sm text-gray-600">
                  <p>Showing {filteredPayments.length} of {payments.length} paid invoices</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
          <div className="inline-flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Loading paid invoices...</span>
          </div>
        </div>
      )}

      {/* Payments Table */}
      {!loading && (
        <div className="bg-white rounded-lg border shadow-sm">
          <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedInvoices(currentPayments.map(payment => payment.id));
                    } else {
                      setSelectedInvoices([]);
                    }
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('invoiceNumber')}
              >
                <div className="flex items-center space-x-1">
                  <span>Invoice</span>
                  {getSortIcon('invoiceNumber')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('customer')}
              >
                <div className="flex items-center space-x-1">
                  <span>Customer/Supplier</span>
                  {getSortIcon('customer')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center space-x-1">
                  <span>Amount</span>
                  {getSortIcon('amount')}
                </div>
              </th>

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>

              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('dueDate')}
              >
                <div className="flex items-center space-x-1">
                  <span>Due Date</span>
                  {getSortIcon('dueDate')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentPayments.map((payment) => (
              <tr key={payment.id} className={`hover:bg-gray-50 ${
                highlightedPaymentId === payment.id 
                  ? 'bg-blue-50 border-l-4 border-blue-500' 
                  : ''
              }`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.includes(payment.id)}
                    onChange={() => toggleInvoiceSelection(payment.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{payment.invoiceNumber}</div>
                  {payment.description && payment.description !== 'No description' && payment.description !== 'No description provided' && (
                  <div className="text-sm text-gray-500">{payment.description}</div>
                  )}
                  {/* Sent/Received indicator */}
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      payment.customer.type === 'customer' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {payment.customer.type === 'customer' ? 'Sent' : 'Received'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {payment.customer.type === 'customer' ? payment.customer.name : payment.supplier.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {payment.customer.type === 'customer' ? 'Customer' : 'Supplier'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {payment.customer.type === 'customer' ? payment.customer.email : payment.supplier.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{formatCurrency(payment.amount)}</div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(payment)}
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(payment.dueDate)}</div>
                  {payment.paymentDate && (
                    <div className="text-xs text-gray-500">
                      Paid: {formatDateTime(payment.paymentDate)}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    {/* Preview Invoice */}
                    <button
                      onClick={() => handlePreviewInvoice(payment)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="Preview Invoice"
                    >
                      <Eye className="w-4 h-4" />
                    </button>

                    {/* Refund Button - Only for fully paid invoices from sent invoices */}
                    {payment.customer.type === 'customer' && !payment.refundStatus && (
                    <button
                        onClick={() => handleOpenRefundModal(payment)}
                        className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                        title="Refund Invoice"
                      >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex-1 text-sm text-center text-gray-700">
                Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredPayments.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <FileText className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {payments.length === 0 ? 'No paid invoices found' : 'No paid invoices match your criteria'}
            </h3>
            <p className="text-gray-500">
              {payments.length === 0 
                ? 'Paid invoices will appear here as payments are completed' 
                : 'Try adjusting your search or filter criteria.'
              }
            </p>
          </div>
        )}
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showPreview && previewInvoice && (
        <InvoicePreview
          invoice={previewInvoice}
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setPreviewInvoice(null);
          }}
          onDownloadPDF={(invoice) => handleDownloadPDF(invoice, { forcePrintablePreview: true })}
          onDownloadExcel={handleDownloadExcel}
        />
      )}

      {/* Refund Modal */}
      {showRefundModal && refundInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Refund Invoice</h2>
                <p className="text-sm text-gray-600 mt-1">Process a full or partial refund for this payment</p>
              </div>
              <button
                onClick={handleCloseRefundModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                disabled={refundLoading}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Invoice Summary Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Invoice Number</p>
                    <p className="text-lg font-bold text-gray-900">{refundInvoice.invoiceNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Amount Paid</p>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(refundInvoice.amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Customer</p>
                    <p className="text-sm font-medium text-gray-900">{refundInvoice.customer.name}</p>
                    <p className="text-xs text-gray-600">{refundInvoice.customer.email}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Payment Date</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(refundInvoice.paymentDate)}</p>
                  </div>
                  {refundInvoice.refundAmount > 0 && (
                    <>
                      <div>
                        <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">Already Refunded</p>
                        <p className="text-lg font-bold text-orange-700">{formatCurrency(refundInvoice.refundAmount)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Remaining Balance</p>
                        <p className="text-lg font-bold text-green-700">{formatCurrency(calculateRemainingBalance(refundInvoice))}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Refund Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Refund Type</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setRefundType('full');
                      setRefundAmount(calculateRemainingBalance(refundInvoice));
                    }}
                    className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                      refundType === 'full'
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    disabled={refundLoading}
                  >
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">Full Refund</p>
                      <p className="text-2xl font-bold text-blue-600 mt-2">
                        {formatCurrency(calculateRemainingBalance(refundInvoice))}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Refund entire remaining amount</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRefundType('partial')}
                    className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                      refundType === 'partial'
                        ? 'border-orange-500 bg-orange-50 shadow-md'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    disabled={refundLoading}
                  >
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">Partial Refund</p>
                      <p className="text-2xl font-bold text-orange-600 mt-2">Custom</p>
                      <p className="text-xs text-gray-600 mt-1">Refund specific amount</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Partial Refund Amount Input */}
              {refundType === 'partial' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Refund Amount
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      (Max: {formatCurrency(calculateRemainingBalance(refundInvoice))})
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={calculateRemainingBalance(refundInvoice)}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg font-semibold"
                      placeholder="0.00"
                      disabled={refundLoading}
                    />
                  </div>
                </div>
              )}

              {/* Refund Reason */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Refund Reason
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Please provide a detailed reason for this refund (e.g., 'Product defect', 'Service not delivered', 'Customer request')..."
                  disabled={refundLoading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  This reason will be saved for audit purposes and shared with the customer.
                </p>
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">Important Information</p>
                    <ul className="text-xs text-yellow-700 mt-2 space-y-1 list-disc list-inside">
                      <li>Refunds are issued from your Stripe account. Ensure you have enough available balance to cover the refund.</li>
                      <li>Refunds will be returned to the payer's original card.</li>
                      <li>If your connected account does not have enough funds, the account may go negative.</li>
                      <li>This does not affect the platform; the platform balance remains neutral.</li>
                      <li>Funds will appear in customer's account within 5-10 business days.</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleCloseRefundModal}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-semibold"
                  disabled={refundLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitRefund}
                  disabled={!refundReason.trim() || refundLoading || (refundType === 'partial' && (!refundAmount || parseFloat(refundAmount) <= 0))}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {refundLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing Refund...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>
                        Process {refundType === 'full' ? 'Full' : 'Partial'} Refund
                        {refundType === 'full' && ` (${formatCurrency(calculateRemainingBalance(refundInvoice))})`}
                        {refundType === 'partial' && refundAmount && ` (${formatCurrency(refundAmount)})`}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Payments;
